/**
 * Per-tenant locale helpers — currency, number/date formatting, address and
 * postcode conventions, phone dial codes and the country-specific labels that
 * appear on forms and customer-facing documents.
 *
 * The companies table stores a free-text `country`; everything else here is
 * derived from it so a tenant in another market gets the right currency
 * symbol, business-identifier label ("ABN" vs "EIN" vs "VAT No."), address
 * vocabulary, postcode validation and distance unit without any schema change.
 * Unknown countries fall back to the Australian defaults the product launched
 * with. `SUPPORTED_COUNTRIES` is the dropdown list used wherever a country is
 * chosen, so new tenants can only pick a value this file understands.
 *
 * Schema note: the `abn` / `acn` columns hold the tenant's primary and
 * secondary business identifiers regardless of country; only the label changes.
 * Likewise `suburb` / `state` / `postcode` hold locality / region / postal code.
 */
import { format as formatDateFns } from 'date-fns';
import { formatInTz, DEFAULT_TIMEZONE } from './timezone';

export interface PostcodeRule {
  /** Accepts the country's postal code formats (case-insensitive, optional space). */
  pattern: RegExp;
  /** Validation message shown when the pattern fails. */
  message: string;
  /** Example shown as the input placeholder. */
  placeholder: string;
  /** Sensible maxLength for the input. */
  maxLength: number;
}

export interface CompanyLocale {
  /** ISO 3166-1 alpha-2 */
  countryCode: string;
  /** Canonical English name; this is the value stored in `companies.country`. */
  name: string;
  /** ISO 4217 */
  currency: string;
  /** BCP 47 tag used for Intl formatting */
  locale: string;
  /** Labels for the `abn` and `acn` company columns on documents */
  taxIdLabels: { abn: string; acn: string };
  /** Placeholder for the primary business identifier */
  taxIdPlaceholder: string;
  /** Labels for the bank-detail columns on invoices */
  bankLabels: { bank_account_name: string; bsb: string; account_number: string };
  /** Placeholder for the `bsb` column (sort code, routing number, ...) */
  bsbPlaceholder: string;
  /** Labels for the `suburb` / `state` / `postcode` columns on forms */
  addressLabels: { locality: string; region: string; postcode: string };
  /** Placeholders for the same three fields */
  addressPlaceholders: { locality: string; region: string };
  /** Postal-code validation for this country */
  postcode: PostcodeRule;
  /** date-fns pattern for long dates on documents, e.g. "8 September 2026" */
  longDate: string;
  /** date-fns pattern for compact dates in tables, e.g. "8 Sep 2026" */
  shortDate: string;
  /** International dialling code without "+" */
  dialCode: string;
  /** Placeholder for phone inputs, in the national format people type */
  phonePlaceholder: string;
  /** Region code for Google Places autocomplete / Geocoding restriction */
  placesRegion: string;
  /** Distance unit shown for service-area radii (storage is always km) */
  distanceUnit: 'km' | 'mi';
  /** 0 = Sunday, 1 = Monday — first day of the week for "this week" reports */
  weekStartsOn: 0 | 1;
  /** Paper size for printed quotes and invoices */
  paper: 'A4' | 'Letter';
  /** IANA timezone a new tenant starts on */
  defaultTimezone: string;
  /** Tax defaults for a new tenant (mirrors handle_new_user in the DB) */
  taxDefaults: { enabled: boolean; rate: number; label: string; inclusive: boolean };
}

const AU_NZ_ADDRESS = { locality: 'Suburb', region: 'State', postcode: 'Postcode' };

const AU: CompanyLocale = {
  countryCode: 'AU',
  name: 'Australia',
  currency: 'AUD',
  locale: 'en-AU',
  taxIdLabels: { abn: 'ABN', acn: 'ACN' },
  taxIdPlaceholder: '12 345 678 901',
  bankLabels: { bank_account_name: 'Account Name', bsb: 'BSB', account_number: 'Account Number' },
  bsbPlaceholder: '123-456',
  addressLabels: AU_NZ_ADDRESS,
  addressPlaceholders: { locality: 'Sydney', region: 'NSW' },
  postcode: { pattern: /^\d{4}$/, message: 'Postcode must be 4 digits', placeholder: '2000', maxLength: 4 },
  longDate: 'd MMMM yyyy',
  shortDate: 'd MMM yyyy',
  dialCode: '61',
  phonePlaceholder: '0412 345 678',
  placesRegion: 'au',
  distanceUnit: 'km',
  weekStartsOn: 1,
  paper: 'A4',
  defaultTimezone: 'Australia/Sydney',
  taxDefaults: { enabled: true, rate: 10, label: 'GST', inclusive: true },
};

const LOCALES: Record<string, CompanyLocale> = {
  AU,
  NZ: {
    countryCode: 'NZ',
    name: 'New Zealand',
    currency: 'NZD',
    locale: 'en-NZ',
    taxIdLabels: { abn: 'NZBN', acn: 'GST No.' },
    taxIdPlaceholder: '9429041234567',
    bankLabels: { bank_account_name: 'Account Name', bsb: 'Bank', account_number: 'Account Number' },
    bsbPlaceholder: '01-0123',
    addressLabels: { locality: 'Suburb', region: 'Region', postcode: 'Postcode' },
    addressPlaceholders: { locality: 'Auckland', region: 'Auckland' },
    postcode: { pattern: /^\d{4}$/, message: 'Postcode must be 4 digits', placeholder: '1010', maxLength: 4 },
    longDate: 'd MMMM yyyy',
    shortDate: 'd MMM yyyy',
    dialCode: '64',
    phonePlaceholder: '021 123 4567',
    placesRegion: 'nz',
    distanceUnit: 'km',
    weekStartsOn: 1,
    paper: 'A4',
    defaultTimezone: 'Pacific/Auckland',
    taxDefaults: { enabled: true, rate: 15, label: 'GST', inclusive: true },
  },
  US: {
    countryCode: 'US',
    name: 'United States',
    currency: 'USD',
    locale: 'en-US',
    taxIdLabels: { abn: 'EIN', acn: 'License No.' },
    taxIdPlaceholder: '12-3456789',
    bankLabels: { bank_account_name: 'Account Name', bsb: 'Routing Number', account_number: 'Account Number' },
    bsbPlaceholder: '021000021',
    addressLabels: { locality: 'City', region: 'State', postcode: 'ZIP Code' },
    addressPlaceholders: { locality: 'Austin', region: 'TX' },
    postcode: { pattern: /^\d{5}(-\d{4})?$/, message: 'ZIP code must be 5 digits (ZIP+4 allowed)', placeholder: '78701', maxLength: 10 },
    longDate: 'MMMM d, yyyy',
    shortDate: 'MMM d, yyyy',
    dialCode: '1',
    phonePlaceholder: '(555) 000-0000',
    placesRegion: 'us',
    distanceUnit: 'mi',
    weekStartsOn: 0,
    paper: 'Letter',
    defaultTimezone: 'America/New_York',
    taxDefaults: { enabled: false, rate: 0, label: 'Sales Tax', inclusive: false },
  },
  CA: {
    countryCode: 'CA',
    name: 'Canada',
    currency: 'CAD',
    locale: 'en-CA',
    taxIdLabels: { abn: 'Business No.', acn: 'GST/HST No.' },
    taxIdPlaceholder: '123456789',
    bankLabels: { bank_account_name: 'Account Name', bsb: 'Transit / Institution', account_number: 'Account Number' },
    bsbPlaceholder: '12345-001',
    addressLabels: { locality: 'City', region: 'Province', postcode: 'Postal Code' },
    addressPlaceholders: { locality: 'Toronto', region: 'ON' },
    postcode: { pattern: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/, message: 'Postal code must look like A1A 1A1', placeholder: 'M5V 3L9', maxLength: 7 },
    longDate: 'MMMM d, yyyy',
    shortDate: 'MMM d, yyyy',
    dialCode: '1',
    phonePlaceholder: '(416) 000-0000',
    placesRegion: 'ca',
    distanceUnit: 'km',
    weekStartsOn: 0,
    paper: 'Letter',
    defaultTimezone: 'America/Toronto',
    taxDefaults: { enabled: true, rate: 13, label: 'HST', inclusive: true },
  },
  GB: {
    countryCode: 'GB',
    name: 'United Kingdom',
    currency: 'GBP',
    locale: 'en-GB',
    taxIdLabels: { abn: 'Company No.', acn: 'VAT No.' },
    taxIdPlaceholder: '12345678',
    bankLabels: { bank_account_name: 'Account Name', bsb: 'Sort Code', account_number: 'Account Number' },
    bsbPlaceholder: '12-34-56',
    addressLabels: { locality: 'Town / City', region: 'County', postcode: 'Postcode' },
    addressPlaceholders: { locality: 'London', region: 'Greater London' },
    postcode: { pattern: /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s?\d[A-Za-z]{2}$/, message: 'Postcode must look like SW1A 1AA', placeholder: 'SW1A 1AA', maxLength: 8 },
    longDate: 'd MMMM yyyy',
    shortDate: 'd MMM yyyy',
    dialCode: '44',
    phonePlaceholder: '07700 900123',
    placesRegion: 'gb',
    distanceUnit: 'mi',
    weekStartsOn: 1,
    paper: 'A4',
    defaultTimezone: 'Europe/London',
    taxDefaults: { enabled: true, rate: 20, label: 'VAT', inclusive: true },
  },
  IE: {
    countryCode: 'IE',
    name: 'Ireland',
    currency: 'EUR',
    locale: 'en-IE',
    taxIdLabels: { abn: 'CRO No.', acn: 'VAT No.' },
    taxIdPlaceholder: '123456',
    bankLabels: { bank_account_name: 'Account Name', bsb: 'BIC', account_number: 'IBAN' },
    bsbPlaceholder: 'AIBKIE2D',
    addressLabels: { locality: 'Town / City', region: 'County', postcode: 'Eircode' },
    addressPlaceholders: { locality: 'Dublin', region: 'Co. Dublin' },
    postcode: { pattern: /^[A-Za-z]\d{2}\s?[A-Za-z\d]{4}$/, message: 'Eircode must look like D02 X285', placeholder: 'D02 X285', maxLength: 8 },
    longDate: 'd MMMM yyyy',
    shortDate: 'd MMM yyyy',
    dialCode: '353',
    phonePlaceholder: '085 123 4567',
    placesRegion: 'ie',
    distanceUnit: 'km',
    weekStartsOn: 1,
    paper: 'A4',
    defaultTimezone: 'Europe/Dublin',
    taxDefaults: { enabled: true, rate: 23, label: 'VAT', inclusive: true },
  },
  SG: {
    countryCode: 'SG',
    name: 'Singapore',
    currency: 'SGD',
    locale: 'en-SG',
    taxIdLabels: { abn: 'UEN', acn: 'GST Reg. No.' },
    taxIdPlaceholder: '201812345A',
    bankLabels: { bank_account_name: 'Account Name', bsb: 'Bank Code', account_number: 'Account Number' },
    bsbPlaceholder: '7171',
    addressLabels: { locality: 'Area', region: 'District', postcode: 'Postal Code' },
    addressPlaceholders: { locality: 'Bishan', region: '20' },
    postcode: { pattern: /^\d{6}$/, message: 'Postal code must be 6 digits', placeholder: '579837', maxLength: 6 },
    longDate: 'd MMMM yyyy',
    shortDate: 'd MMM yyyy',
    dialCode: '65',
    phonePlaceholder: '9123 4567',
    placesRegion: 'sg',
    distanceUnit: 'km',
    weekStartsOn: 1,
    paper: 'A4',
    defaultTimezone: 'Asia/Singapore',
    taxDefaults: { enabled: true, rate: 9, label: 'GST', inclusive: true },
  },
  ZA: {
    countryCode: 'ZA',
    name: 'South Africa',
    currency: 'ZAR',
    locale: 'en-ZA',
    taxIdLabels: { abn: 'Reg. No.', acn: 'VAT No.' },
    taxIdPlaceholder: '2018/123456/07',
    bankLabels: { bank_account_name: 'Account Name', bsb: 'Branch Code', account_number: 'Account Number' },
    bsbPlaceholder: '250655',
    addressLabels: { locality: 'Suburb', region: 'Province', postcode: 'Postal Code' },
    addressPlaceholders: { locality: 'Sandton', region: 'Gauteng' },
    postcode: { pattern: /^\d{4}$/, message: 'Postal code must be 4 digits', placeholder: '2196', maxLength: 4 },
    longDate: 'd MMMM yyyy',
    shortDate: 'd MMM yyyy',
    dialCode: '27',
    phonePlaceholder: '082 123 4567',
    placesRegion: 'za',
    distanceUnit: 'km',
    weekStartsOn: 1,
    paper: 'A4',
    defaultTimezone: 'Africa/Johannesburg',
    taxDefaults: { enabled: true, rate: 15, label: 'VAT', inclusive: true },
  },
};

const COUNTRY_ALIASES: Record<string, string> = {
  au: 'AU', aus: 'AU', australia: 'AU',
  nz: 'NZ', 'new zealand': 'NZ',
  us: 'US', usa: 'US', 'united states': 'US', 'united states of america': 'US', america: 'US',
  ca: 'CA', canada: 'CA',
  gb: 'GB', uk: 'GB', 'united kingdom': 'GB', england: 'GB', scotland: 'GB', wales: 'GB', 'great britain': 'GB',
  'northern ireland': 'GB', britain: 'GB', 'u.k.': 'GB',
  ie: 'IE', ireland: 'IE', eire: 'IE',
  sg: 'SG', singapore: 'SG',
  za: 'ZA', 'south africa': 'ZA',
};

export const DEFAULT_COMPANY_LOCALE: CompanyLocale = AU;

/** Countries a tenant can be created in, in dropdown order. */
export const SUPPORTED_COUNTRIES: CompanyLocale[] = [
  LOCALES.AU, LOCALES.NZ, LOCALES.US, LOCALES.CA, LOCALES.GB, LOCALES.IE, LOCALES.SG, LOCALES.ZA,
];

/** True when the stored country string maps to a supported market. */
export function isSupportedCountry(country: string | null | undefined): boolean {
  const key = (country ?? '').trim().toLowerCase();
  if (!key) return false;
  const code = COUNTRY_ALIASES[key] ?? key.toUpperCase();
  return code in LOCALES;
}

/** Resolve a locale config from the free-text `companies.country` value. */
export function resolveCompanyLocale(country: string | null | undefined): CompanyLocale {
  const key = (country ?? '').trim().toLowerCase();
  if (!key) return DEFAULT_COMPANY_LOCALE;
  const code = COUNTRY_ALIASES[key] ?? key.toUpperCase();
  return LOCALES[code] ?? DEFAULT_COMPANY_LOCALE;
}

/** Canonical stored value for a country string ("uk" → "United Kingdom"). */
export function canonicalCountryName(country: string | null | undefined): string {
  return resolveCompanyLocale(country).name;
}

/** "$1,234.50" / "US$1,234.50" / "£1,234.50" according to the tenant's market. */
export function formatMoney(amount: number | null | undefined, locale: CompanyLocale = DEFAULT_COMPANY_LOCALE): string {
  const value = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  return new Intl.NumberFormat(locale.locale, { style: 'currency', currency: locale.currency }).format(value);
}

/** Currency code shown once on a document ("Amounts in AUD"). */
export function currencyCode(locale: CompanyLocale = DEFAULT_COMPANY_LOCALE): string {
  return locale.currency;
}

/** Postal-code check for the tenant's country; empty values are left to the caller. */
export function isValidPostcode(value: string | null | undefined, locale: CompanyLocale = DEFAULT_COMPANY_LOCALE): boolean {
  const v = (value ?? '').trim();
  if (!v) return true;
  return locale.postcode.pattern.test(v);
}

/** Radius helpers: metros store km; the UI shows the tenant's unit. */
const MI_PER_KM = 0.621371;
export function kmToDisplayDistance(km: number, locale: CompanyLocale = DEFAULT_COMPANY_LOCALE): number {
  return locale.distanceUnit === 'mi' ? km * MI_PER_KM : km;
}
export function displayDistanceToKm(value: number, locale: CompanyLocale = DEFAULT_COMPANY_LOCALE): number {
  return locale.distanceUnit === 'mi' ? value / MI_PER_KM : value;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Format a stored date for a document.
 *
 * Date-only columns (issue_date, due_date, valid_until, paid_date) are calendar
 * dates with no timezone; they must never be run through `new Date('YYYY-MM-DD')`
 * because that parses as UTC midnight and shifts a day back for viewers west of
 * Greenwich. Timestamps (created_at, signed_at) are rendered in the company
 * timezone so the date matches what the business saw when it acted.
 */
export function formatDocDate(
  value: string | Date | null | undefined,
  pattern: string,
  timezone: string = DEFAULT_TIMEZONE,
): string {
  if (!value) return '';
  if (typeof value === 'string' && DATE_ONLY.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return formatDateFns(new Date(y, m - 1, d), pattern);
  }
  try {
    return formatInTz(value, timezone, pattern);
  } catch {
    return '';
  }
}

/** True when a date-only string is strictly before today's calendar date in the given timezone. */
export function isDateOnlyPast(value: string | null | undefined, timezone: string = DEFAULT_TIMEZONE): boolean {
  if (!value || !DATE_ONLY.test(value)) return false;
  let today: string;
  try {
    today = formatInTz(new Date(), timezone, 'yyyy-MM-dd');
  } catch {
    today = formatDateFns(new Date(), 'yyyy-MM-dd');
  }
  return value < today;
}
